/* eslint max-lines: ["error", { "max": 800 }] */
import {
  CitizenshipStatus,
  EmploymentType,
  Gender,
  MaritalStatus,
  PossibleFipsCode,
  WageType,
} from '@zorro/clients';
import {
  RequiredClassCategories,
  VALIDATION_MESSAGES,
  getRequiredClassCategoriesForTargetYear,
  hasCustomClassCategories,
  insuredMaxDateOfBirth,
  insuredMinDateOfBirth,
  parsePhoneNumber,
} from '@zorro/common-utils';
import { Checkbox } from '@zorro/design-system/v2/Checkbox';
import { Divider } from '@zorro/design-system/v2/Divider';
import { RadioGroup } from '@zorro/design-system/v2/Radio';
import { Select } from '@zorro/design-system/v2/Select';
import { SsnPasswordInput } from '@zorro/design-system/v2/SsnPasswordInput';
import { TextInput } from '@zorro/design-system/v2/TextInput';
import { DateUtilInstance, getNowAsDate } from '@zorro/shared/formatters';
import {
  AddressInput,
  DateInput,
  FormErrorMessage,
  Grid,
  Group,
  PhoneInput,
  Text,
} from '@zorro/web/design-system';
import {
  CitizenshipStatusInput,
  EmploymentTypeInput,
  FullPageLoader,
  MaritalStatusInput,
} from '@zorro/web/shared-ui';
import {
  extractCustomClassCategories,
  useMonolithQuery,
} from '@zorro/web/ui-utils';
import { Controller, UseFormReturn } from 'react-hook-form';
import * as yup from 'yup';

import { WageTypeInput } from '../WageTypeInput';
import { AnticipatedCareLevelInput } from './AnticipatedCareLevelInput';
import {
  InsuredFormGenderRadios,
  InsuredFormYesNoRadios,
} from './InsuredFormRadios';
import { getInsuredBaseSchema } from './InsuredFormUtils';

export const getEmployeeFormSchema = (
  isFinalizationMode: boolean,
  withAnticipatedCareLevel: boolean,
  requiredClassCategories?: RequiredClassCategories
) =>
  getInsuredBaseSchema(
    isFinalizationMode,
    true,
    withAnticipatedCareLevel
  ).concat(
    yup.object({
      email: yup
        .string()
        .email(VALIDATION_MESSAGES.emailRequired)
        .matches(/^[^\s@]+@[^\s@][^\s.@]*\.[^\s@]{2,}$/u, {
          message: VALIDATION_MESSAGES.emailRequired,
        })
        .required(VALIDATION_MESSAGES.emailRequired),
      isMailingAddressSameAsResidentialAddress: yup
        .boolean()
        .required(VALIDATION_MESSAGES.isMailingAddressSameAsResidentialAddress),
      employmentType: requiredClassCategories?.hasEmploymentType
        ? yup
            .mixed<EmploymentType>()
            .required(VALIDATION_MESSAGES.employmentTypeRequired)
        : yup.mixed<EmploymentType>().nullable(),
      fipsCode: yup.string().optional(),
      wageType: requiredClassCategories?.hasWageType
        ? yup.mixed<WageType>().required(VALIDATION_MESSAGES.wageTypeRequired)
        : yup.mixed<WageType>().nullable(),
      customClassCategory: requiredClassCategories?.hasCustomClassCategory
        ? yup.string().required(VALIDATION_MESSAGES.customClassCategoryRequired)
        : yup.string().nullable(),
      phone: yup
        .string()
        .required(VALIDATION_MESSAGES.phoneNumberRequired)
        /**
         * We expect a 10-digit US phone number entered by the user
         * and react-phone-number-input automatically adds +1 prefix to it
         */
        .test(
          'valid-phone',
          VALIDATION_MESSAGES.phoneNumberValid,
          (value) => parsePhoneNumber(value) !== undefined
        ),
      personalEmail: yup
        .string()
        .email(VALIDATION_MESSAGES.personalEmailValid)
        .matches(/^[^\s@]+@[^\s@][^\s.@]*\.[^\s@]{2,}$/u, {
          message: VALIDATION_MESSAGES.personalEmailValid,
          excludeEmptyString: true,
        })
        .required(VALIDATION_MESSAGES.personalEmailRequired),
      mailingAddress: yup
        .string()
        .when('isMailingAddressSameAsResidentialAddress', {
          is: false,
          then: () =>
            yup.string().required(VALIDATION_MESSAGES.mailingAddressRequired),
          otherwise: () => yup.string().notRequired(),
        }),
      // Nullable in the type because the form starts empty; the test still requires a value.
      citizenshipStatus: yup
        .mixed<CitizenshipStatus>()
        .oneOf(
          Object.values(CitizenshipStatus),
          VALIDATION_MESSAGES.citizenshipStatus
        )
        .typeError(VALIDATION_MESSAGES.citizenshipStatus)
        .nullable()
        .test('required', VALIDATION_MESSAGES.citizenshipStatus, Boolean),
      maritalStatus: yup
        .mixed<MaritalStatus>()
        .oneOf(Object.values(MaritalStatus), VALIDATION_MESSAGES.maritalStatus)
        .typeError(VALIDATION_MESSAGES.maritalStatus)
        .nullable()
        .test('required', VALIDATION_MESSAGES.maritalStatus, Boolean),
    })
  );

export type InsuredEmployeeFormFields = yup.InferType<
  ReturnType<typeof getEmployeeFormSchema>
>;

type Props = {
  employeeForm: UseFormReturn<InsuredEmployeeFormFields>;
  targetEnrollmentDate?: DateUtilInstance;
  isFinalizationMode: boolean;
  isEmployeeMode: boolean;
  employerId: string;
  possibleFipsCodes: PossibleFipsCode[];
  setPossibleFipsCodes: (fipsCodes: PossibleFipsCode[]) => void;
  isGlobalMode: boolean;
  withAnticipatedCareLevel?: boolean;
  withResidentialAddress?: boolean;
  onAddressChange: () => Promise<void>;
};

export const EmployeeFormInputs = ({
  employeeForm,
  targetEnrollmentDate,
  employerId,
  isEmployeeMode,
  isFinalizationMode,
  possibleFipsCodes,
  setPossibleFipsCodes,
  isGlobalMode,
  withAnticipatedCareLevel,
  withResidentialAddress = true,
  onAddressChange,
}: Props) => {
  'use no memo';
  const { control, watch, formState, setValue } = employeeForm;
  const { errors } = formState;

  const isFemale = watch('gender') === Gender.FEMALE;
  const isMailingAddressSameAsResidentialAddress = watch(
    'isMailingAddressSameAsResidentialAddress'
  );

  const {
    data: { classDefinitions } = { classDefinitions: [] },
    isLoading: isLoadingClassDefinitions,
  } = useMonolithQuery({
    method: 'allowanceModelControllerGetAllClassDefinitionsForEmployer',
    params: employerId && [employerId],
  });

  const now = getNowAsDate();
  const currentYear = now.getFullYear();

  const targetAllowanceYear = targetEnrollmentDate?.toDate().getFullYear();

  const uniqueCustomClassCategoryNames = extractCustomClassCategories(
    classDefinitions,
    isGlobalMode ? currentYear : (targetAllowanceYear ?? currentYear),
    isGlobalMode
  );

  const showCustomClassCategory = hasCustomClassCategories(
    uniqueCustomClassCategoryNames
  );

  const allowanceYears = isGlobalMode
    ? [currentYear, currentYear + 1]
    : targetAllowanceYear
      ? [targetAllowanceYear]
      : [];

  const requiredClassCategories = getRequiredClassCategoriesForTargetYear(
    classDefinitions,
    allowanceYears
  );

  if (isLoadingClassDefinitions) {
    return <FullPageLoader />;
  }

  return (
    <Group>
      <Grid>
        <Grid.Col span={{ sm: 6 }}>
          <Controller
            control={control}
            name="firstName"
            render={({ field: { ...rest } }) => (
              <TextInput
                {...rest}
                label="First name"
                placeholder="First name"
                required
                isMasked
                size="lg"
              />
            )}
          />

          <FormErrorMessage errors={errors} fieldName="firstName" />
        </Grid.Col>

        <Grid.Col span={{ sm: 6 }}>
          <Controller
            control={control}
            name="lastName"
            render={({ field: { ...rest } }) => (
              <TextInput
                {...rest}
                label="Last name"
                placeholder="Last name"
                required
                isMasked
                size="lg"
              />
            )}
          />

          <FormErrorMessage errors={errors} fieldName="lastName" />
        </Grid.Col>

        <Grid.Col span={{ sm: 6 }}>
          <Controller
            control={control}
            name="dateOfBirth"
            render={({ field: { ...rest } }) => {
              return (
                <DateInput
                  {...rest}
                  label="Date of birth"
                  minDate={insuredMinDateOfBirth(false, targetEnrollmentDate)}
                  maxDate={insuredMaxDateOfBirth(false, targetEnrollmentDate)}
                  withCalendarIcon={false}
                  required
                  size="lg"
                  isMasked
                />
              );
            }}
          />

          <FormErrorMessage errors={errors} fieldName="dateOfBirth" />
        </Grid.Col>

        <Grid.Col span={{ sm: 6 }}>
          <Controller
            control={control}
            name="phone"
            render={({ field }) => (
              <PhoneInput {...field} isRequired size="lg" />
            )}
          />

          <FormErrorMessage errors={errors} fieldName="phone" />
        </Grid.Col>

        <Grid.Col span={{ sm: 6 }}>
          <Controller
            control={control}
            name="gender"
            render={({ field }) => (
              <RadioGroup
                {...field}
                label="Gender"
                required={isFinalizationMode}
              >
                <InsuredFormGenderRadios />
              </RadioGroup>
            )}
          />

          <FormErrorMessage errors={errors} fieldName="gender" />
        </Grid.Col>

        <Grid.Col span={{ sm: 6 }}>
          <Divider hiddenFrom="sm" mt="var(--space-8)" mb="var(--space-16)" />

          <Controller
            control={control}
            name="isSmoker"
            render={({ field }) => (
              <RadioGroup
                {...field}
                label="Tobacco user"
                required={isFinalizationMode}
              >
                <InsuredFormYesNoRadios />
              </RadioGroup>
            )}
          />

          <FormErrorMessage errors={errors} fieldName="isSmoker" />
        </Grid.Col>

        {isFemale && (
          <>
            <Grid.Col span={{ sm: 6 }}>
              <Divider
                hiddenFrom="sm"
                mt="var(--space-8)"
                mb="var(--space-16)"
              />

              <Controller
                control={control}
                name="isPregnant"
                render={({ field }) => (
                  <RadioGroup
                    {...field}
                    label="Pregnant"
                    required={isFinalizationMode}
                  >
                    <InsuredFormYesNoRadios />
                  </RadioGroup>
                )}
              />

              <FormErrorMessage errors={errors} fieldName="isPregnant" />
            </Grid.Col>
            <Grid.Col span={{ sm: 6 }} />
          </>
        )}

        <Grid.Col span={{ sm: 6 }}>
          <Controller
            control={control}
            name="email"
            render={({ field: { ...rest } }) => (
              <TextInput
                {...rest}
                isReadOnly
                readOnlyVariant="lock"
                label="Company email"
                placeholder="Email"
                required
                size="lg"
              />
            )}
          />

          <FormErrorMessage errors={errors} fieldName="email" />
        </Grid.Col>

        <Grid.Col span={{ sm: 6 }}>
          <Controller
            control={control}
            name="personalEmail"
            render={({ field: { ...rest } }) => (
              <TextInput
                {...rest}
                required
                value={rest.value ?? undefined}
                label="Personal email"
                placeholder="Email"
                size="lg"
              />
            )}
          />
          <FormErrorMessage errors={errors} fieldName="personalEmail" />
        </Grid.Col>

        {withResidentialAddress && (
          <>
            <Grid.Col>
              <Controller
                control={control}
                name="residentialAddress"
                render={({ field: { value, onBlur, onChange, ...rest } }) => (
                  <AddressInput
                    {...rest}
                    onBlur={async () => {
                      onChange(value);
                      onBlur();
                      setValue('fipsCode', undefined);
                      setPossibleFipsCodes([]);
                      await onAddressChange();
                    }}
                    required
                    notifySubscribers={(newAddress) => onChange(newAddress)}
                    onChange={onChange}
                    label="Residential address"
                    value={value || ''}
                    placeholder="Type residential address"
                    size="lg"
                    isMasked
                  />
                )}
              />
              <FormErrorMessage
                errors={errors}
                fieldName="residentialAddress"
              />
            </Grid.Col>
            {possibleFipsCodes.length > 1 && (
              <Grid.Col>
                <Controller
                  control={control}
                  name="fipsCode"
                  render={({ field: { ...rest } }) => (
                    <Select
                      {...rest}
                      label="County of residence"
                      placeholder="Select county of residence"
                      data={possibleFipsCodes.map(({ fipsCode, county }) => ({
                        label: county,
                        value: fipsCode,
                      }))}
                    />
                  )}
                />
                <FormErrorMessage errors={errors} fieldName="fipsCode" />
              </Grid.Col>
            )}

            <Grid.Col>
              <Controller
                control={control}
                name="isMailingAddressSameAsResidentialAddress"
                render={({ field: { value, ...rest } }) => (
                  <Checkbox
                    {...rest}
                    checked={value}
                    label="Mailing address is the same as residential address"
                  />
                )}
              />

              <FormErrorMessage
                errors={errors}
                fieldName="isMailingAddressSameAsResidentialAddress"
              />
            </Grid.Col>

            {!isMailingAddressSameAsResidentialAddress && (
              <Grid.Col>
                <Controller
                  control={control}
                  name="mailingAddress"
                  render={({ field: { onChange, ...rest } }) => (
                    <AddressInput
                      {...rest}
                      notifySubscribers={(address: string) => onChange(address)}
                      onChange={onChange}
                      required
                      placeholder="Type mailing address"
                      label="Mailing address"
                      size="lg"
                      isMasked
                    />
                  )}
                />

                <FormErrorMessage errors={errors} fieldName="mailingAddress" />
              </Grid.Col>
            )}
          </>
        )}

        <Grid.Col span={{ sm: 6 }}>
          <Controller
            control={control}
            name="ssn"
            render={({ field: { value, ...rest } }) => (
              <SsnPasswordInput
                {...rest}
                label="SSN"
                value={value || ''}
                required
                size="lg"
              />
            )}
          />

          <FormErrorMessage errors={errors} fieldName="ssn" />
        </Grid.Col>

        <Grid.Col span={{ sm: 6 }}>
          <Controller
            name="citizenshipStatus"
            control={control}
            render={({ field }) => (
              <CitizenshipStatusInput
                {...field}
                selectProps={{
                  required: true,
                  clearable: false,
                }}
              />
            )}
          />

          <FormErrorMessage errors={errors} fieldName="citizenshipStatus" />
        </Grid.Col>

        <Grid.Col span={{ sm: 6 }}>
          <Controller
            name="maritalStatus"
            control={control}
            render={({ field }) => (
              <MaritalStatusInput
                {...field}
                selectProps={{
                  required: true,
                  clearable: false,
                }}
              />
            )}
          />

          <FormErrorMessage errors={errors} fieldName="maritalStatus" />
        </Grid.Col>

        {!isEmployeeMode && (
          <>
            <Grid.Col mt="lg">
              <Text fw={600}>Employment info</Text>
            </Grid.Col>

            <Grid.Col span={{ sm: 6 }}>
              <Controller
                name="employmentType"
                control={control}
                render={({ field }) => (
                  <EmploymentTypeInput
                    {...field}
                    selectProps={{
                      required: requiredClassCategories.hasEmploymentType,
                      clearable: !requiredClassCategories.hasEmploymentType,
                    }}
                  />
                )}
              />
              <FormErrorMessage errors={errors} fieldName="employmentType" />
            </Grid.Col>
            <Grid.Col span={{ sm: 6 }}>
              <Controller
                name="wageType"
                control={control}
                render={({ field }) => (
                  <WageTypeInput
                    {...field}
                    selectProps={{
                      required: requiredClassCategories.hasWageType,
                      clearable: !requiredClassCategories.hasWageType,
                    }}
                  />
                )}
              />
              <FormErrorMessage errors={errors} fieldName="wageType" />
            </Grid.Col>
            {showCustomClassCategory && (
              <Grid.Col span={{ sm: 6 }}>
                <Controller
                  control={control}
                  name="customClassCategory"
                  render={({ field: { ...rest } }) => (
                    <Select
                      {...rest}
                      size="lg"
                      label="Custom class category"
                      placeholder="Select a custom class category"
                      data={uniqueCustomClassCategoryNames.map(
                        (className: string) => ({
                          value: className,
                          label: className,
                        })
                      )}
                      required
                    />
                  )}
                />
                <FormErrorMessage
                  errors={errors}
                  fieldName="customClassCategory"
                />
              </Grid.Col>
            )}
          </>
        )}
        {withAnticipatedCareLevel && (
          <Grid.Col>
            <Divider mt="var(--space-12)" mb="var(--space-20)" />
            <Controller
              control={control}
              name="anticipatedCareLevel"
              render={({ field }) => (
                <AnticipatedCareLevelInput
                  {...field}
                  isRequired={isFinalizationMode}
                />
              )}
            />
            <FormErrorMessage
              errors={errors}
              fieldName="anticipatedCareLevel"
            />
          </Grid.Col>
        )}
      </Grid>
    </Group>
  );
};
